import React, { lazy, Suspense } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import GlobalStyle from "./styles/GlobalStyle";
import './assets/fonts/fonts.css';
import PageContainer from './components/layout/PageContainer';
import ScrollToTop from './components/layout/ScrollToTop';
import SkipLink from './components/ui/SkipLink';
import NotFound from "./pages/NotFound.jsx";
import ClientOnly from './components/ClientOnly';
import useKonamiCode from './hooks/useKonamiCode';

// Lazy load SpacetimeGrid to defer Three.js loading (264KB)
// Wrapped in ClientOnly to prevent SSG rendering
const SpacetimeGrid = lazy(() => import('./components/layout/SpacetimeGrid'));

// Static imports for core pages (frequently visited)
import HomePage from './pages/HomePage';
import ProjectPage from './pages/ProjectPage';
import AboutPage from './pages/AboutPage';

// Lazy load contact page (less frequently visited)
const ContactPage = lazy(() => import('./pages/ContactPage'));

// Lazy load ALL project pages to reduce main bundle
// These will still be pre-rendered during SSG but loaded on-demand on client
const SnobifyPage = lazy(() => import('./pages/SnobifyPage'));
const RSOPage = lazy(() => import('./pages/RSOPage'));
const ACatGamePage = lazy(() => import('./pages/ACatGame'));
const AlmondoPage = lazy(() => import('./pages/AlmondoPage'));
const AdaptiveAestheticsPage = lazy(() => import('./pages/AdaptiveAestheticsPage'));
const PukapokPage = lazy(() => import('./pages/PukaPokPage'));
const KissCamPage = lazy(() => import('./pages/KissCamPage'));
const FaustStudioPage = lazy(() => import('./pages/FaustStudio'));

// Lazy load rarely-visited legal pages
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));

// Dev-only pages (hidden in production)
const OgPreviewPage = lazy(() => import('./pages/OgPreviewPage'));
const OgRsoPreviewPage = lazy(() => import('./pages/OgRsoPreviewPage'));
const IconPreviewPage = lazy(() => import('./pages/IconPreviewPage'));
const PaddleTestPage = lazy(() => import('./pages/PaddleTestPage'));
const RsoTermsPage = lazy(() => import('./pages/RsoTermsPage'));
const WebsiteTermsPage = lazy(() => import('./pages/WebsiteTermsPage'));
const KissCamTermsPage = lazy(() => import('./pages/KissCamTermsPage'));
const CometGamePage = lazy(() => import('./pages/CometGamePage'));
const RsoOfflineActivationPage = lazy(() => import('./pages/RsoOfflineActivationPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));

// Detect production environment - hides dev-only routes (og-preview, paddle-test) in production
const isProduction = typeof window !== 'undefined' &&
    (window.location.hostname === 'lperron.com' || window.location.hostname === 'www.lperron.com');

const AppContainer = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    position: relative;
    /* Use overflow: clip instead of hidden to allow sticky positioning */
    overflow-x: clip;
`;

const ContentWrapper = styled.div`
    position: relative;
    z-index: 1;
    flex: 1;
    display: flex;
    flex-direction: column;
`;

const MainContent = styled.main`
    flex: 1;
    width: 100%;
`;

// The layout component that wraps all pages
export function AppLayout() {
    // Easter egg: Konami code navigates to the Comet game
    useKonamiCode();

    return (
        <>
            <GlobalStyle />
            <ScrollToTop />
            <AppContainer>
                <SkipLink href="#main-content">Skip to main content</SkipLink>
                {/* Client-only lazy load to exclude from SSG and defer Three.js (264KB) */}
                <ClientOnly>
                    <Suspense fallback={null}>
                        <SpacetimeGrid is404Page={false} />
                    </Suspense>
                </ClientOnly>
                <Header />
                <ContentWrapper>
                    <MainContent id="main-content">
                        <PageContainer>
                            <Outlet />
                        </PageContainer>
                    </MainContent>
                    <Footer />
                </ContentWrapper>
            </AppContainer>
        </>
    );
}

function NotFoundLayout() {
    // Easter egg: Konami code navigates to the Comet game
    useKonamiCode();

    return (
        <AppContainer>
            <SkipLink href="#main-content">Skip to main content</SkipLink>
            <ClientOnly>
                <Suspense fallback={null}>
                    <SpacetimeGrid is404Page={true} />
                </Suspense>
            </ClientOnly>
            <Header />
            <ContentWrapper>
                <MainContent id="main-content">
                    <PageContainer>
                        <NotFound />
                    </PageContainer>
                </MainContent>
                <Footer />
            </ContentWrapper>
        </AppContainer>
    );
}

// Export routes configuration for vite-react-ssg
export const routes = [
    {
        path: '/',
        element: <AppLayout />,
        children: [
            { index: true, Component: HomePage },
            { path: 'projects', Component: ProjectPage },
            
            // Specific Project Routes (Canonical)
            { path: 'projects/snobify', Component: SnobifyPage },
            { path: 'projects/rso', Component: RSOPage },
            { path: 'projects/a-cat-game', Component: ACatGamePage },
            { path: 'projects/almondo', Component: AlmondoPage },
            { path: 'projects/adaptive-aesthetics', Component: AdaptiveAestheticsPage },
            { path: 'projects/pukapok', Component: PukapokPage },
            { path: 'projects/kisscam', Component: KissCamPage },
            { path: 'projects/faust-studio', Component: FaustStudioPage },

            // Dynamic fallback for generic projects or future ones
            { path: 'projects/:projectId', Component: ProjectPage },

            { path: 'about', Component: AboutPage },
            { path: 'contact', Component: ContactPage },
            { path: 'privacy-policy', Component: PrivacyPolicyPage },
            { path: 'terms-of-use', Component: WebsiteTermsPage },
            { path: 'rso-terms-of-service', Component: RsoTermsPage },
            { path: 'kisscam-terms-of-use', Component: KissCamTermsPage },
            { path: 'rso-offline-activation', Component: RsoOfflineActivationPage },
            { path: 'checkout', Component: CheckoutPage },
            { path: 'thank-you', Component: ThankYouPage },

            // Dev-only routes (hidden in production)
            ...(!isProduction ? [
                { path: 'og-preview', Component: OgPreviewPage },
                { path: 'og-preview/rso', Component: OgRsoPreviewPage },
                { path: 'og-preview/icons', Component: IconPreviewPage },
                { path: 'paddle-test', Component: PaddleTestPage },
            ] : []),

            // Easter egg (Konami code)
            { path: 'comet-game', Component: CometGamePage },

            // Catch-all for unmatched routes - must be last in children
            { path: '*', element: <Navigate to="/404" replace /> },
        ],
    },
    {
        path: '/404',
        element: <NotFoundLayout />,
    },
];
